import { useMemo } from "react"
import {
	Stack, StackProps,
	Card,
	Typography,
	CardProps, CardActionArea
} from "@mui/material"

import {
	AccessTime as LoadingIcon,
	Person as KojinMatchIcon,
	PersonAdd as KojinMultiHitIcon,
	Domain as SisetuMatchIcon,
	DomainAdd as SisetuMultiHitIcon,
	Error as MayBeIcon,
	Help as UnableIcon,
	Clear as NotFoundIcon,
  Comment as MemoIcon,
} from '@mui/icons-material'

import { useMatching } from "../container"
import { MatchingState } from "../types"

type MatchingStatsCellProps = {
	target: 'kojin' | 'sisetu',
	state: MatchingState,
	count: number,
	selectedState: MatchingState | null,
	onChangeSelectedState: (state: MatchingState | null) => void
} & CardProps
const MatchingStatsCell = ({
	target,
	state,
	count,
	selectedState,
	onChangeSelectedState,
	...others
}: MatchingStatsCellProps) => {
	const icon = useMemo(
		() => {
			if (state === "match") return target === 'kojin' ? <KojinMatchIcon color="success"/> : <SisetuMatchIcon color="success"/>
			if (state === "multiHit") return target === 'kojin' ? <KojinMultiHitIcon color="secondary"/> : <SisetuMultiHitIcon color="secondary"/>
			if (state === "maybe") return <MayBeIcon color="warning"/>
			if (state === "unable") return <UnableIcon color="error"/>
			if (state === "notFound") return <NotFoundIcon color="disabled"/>
			if (state === "wait") return <LoadingIcon color="disabled"/>
			if (state === "memo") return <MemoIcon color="success"/>
			return <></>
		},
		[state, target]
	)
	return (
		<Card variant="outlined" sx={{ display: 'flex' }} {...others}>
			<CardActionArea
				data-active={selectedState === state ? '' : undefined}
				onClick={() => onChangeSelectedState(state === selectedState ? null : state)}
				sx={{
					'&[data-active]': {
						backgroundColor: 'action.selected',
						'&:hover': {
							backgroundColor: 'action.selectedHover',
						},
					},
				}}
			>
				<Stack direction="row" padding={1} spacing={1} alignItems="center">
					{icon}
					<Typography variant="body2">{count}</Typography>
				</Stack>
			</CardActionArea>
		</Card>
	)
}

type MatchingFilterStateProps = {
	container: ReturnType<typeof useMatching>
} & StackProps
export const MatchingFilterState = ({
	container: {
		condition,
		matchingStats,
		filteredState, setFilterdState
	},
	...others
}: MatchingFilterStateProps) => {
	return condition ? (
		<Stack {...others}>
			{matchingStats?.match ? (
				<MatchingStatsCell
					target={condition.target}
					state="match"
					count={matchingStats.match}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}
				/>
				) : <></> }
			{matchingStats?.multiHit ? (
				<MatchingStatsCell
					target={condition.target}
					state="multiHit"
					count={matchingStats.multiHit}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}
				/>
			) : <></> }
			{matchingStats?.maybe ? (
				<MatchingStatsCell
					target={condition.target}
					state="maybe"
					count={matchingStats.maybe}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}
				/>
				) : <></> }
			{matchingStats?.unable ? (
				<MatchingStatsCell
					target={condition.target}
					state="unable"
					count={matchingStats.unable}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}/>
				) : <></> }
			{matchingStats?.notFound ? (
				<MatchingStatsCell
					target={condition.target}
					state="notFound"
					count={matchingStats.notFound}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}/>
				) : <></> }
			{matchingStats?.wait ? (
				<MatchingStatsCell
					target={condition.target}
					state="wait"
					count={matchingStats.wait}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}
				/>
			) : <></> }
			{matchingStats?.memo ? (
				<MatchingStatsCell
					target={condition.target}
					state="memo"
					count={matchingStats.memo}
					selectedState={filteredState}
					onChangeSelectedState={setFilterdState}
				/>
				) : <></> }
		</Stack>
	) : <></>
}